import { useState, useEffect, useCallback } from "react";
// import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  axiosGetCompanies,
  axiosUpdateCompany,
  axiosDeleteCompany,
  axiosGetCompanyTemplates,
  axiosGetCompanyTemplateDetails,
  axiosCreateCompanyTemplate,
  axiosUpdateCompanyTemplate,
  axiosDeleteCompanyTemplate,
  axiosGetCategories,
  axiosGetSkills,
  axiosGetQuestions,
} from "../../helpers/superAdminAxios";

const CompanyManagement = () => {
  const { token, notify } = useOutletContext();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // Template Management State
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [templateStructure, setTemplateStructure] = useState([]);
  const [isBuilderLoading, setIsBuilderLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [activeTab, setActiveTab] = useState("categories");
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [newTemplateTitle, setNewTemplateTitle] = useState("");

  // const { t } = useTranslation();

  const loadCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosGetCompanies({
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data);
    } catch (error) {
      console.error("Error loading companies:", error);
      notify("Failed to load companies", "error");
    } finally {
      setIsLoading(false);
    }
  }, [token, notify]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleEditCompany = (company) => {
    setEditingCompany({ ...company });
  };

  const handleSaveEdit = async () => {
    try {
      await axiosUpdateCompany({
        url: `/${editingCompany.id}`,
        headers: { Authorization: `Bearer ${token}` },
        data: { data: editingCompany },
      });
      notify("Company updated successfully", "success");
      setEditingCompany(null);
      loadCompanies();
    } catch (error) {
      console.error("Error updating company:", error);
      notify("Failed to update company", "error");
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${companyName}"? This action cannot be undone and will delete all related data.`
      )
    ) {
      try {
        await axiosDeleteCompany({
          url: `/${companyId}`,
          headers: { Authorization: `Bearer ${token}` },
        });
        notify("Company deleted successfully", "success");
        loadCompanies();
        if (selectedCompany?.id === companyId) {
          setSelectedCompany(null);
          setShowDetails(false);
        }
      } catch (error) {
        console.error("Error deleting company:", error);
        notify("Failed to delete company", "error");
      }
    }
  };

  // Template Management Functions
  const loadTemplates = useCallback(
    async (companyId, showError = true) => {
      try {
        const response = await axiosGetCompanyTemplates({
          token,
          params: { companyId },
        });
        setTemplates(response.data?.data || []);
      } catch (error) {
        if (showError) {
          notify("Failed to load templates", "error");
        }
        console.error("Error loading templates:", error);
      }
    },
    [token, notify]
  );

  const loadAvailableContent = useCallback(async () => {
    setIsBuilderLoading(true);
    try {
      const [categoriesRes, skillsRes, questionsRes] = await Promise.all([
        axiosGetCategories({
          token,
          params: selectedCompany ? { companyId: selectedCompany.id } : {},
        }),
        axiosGetSkills({
          token,
          params: selectedCompany ? { companyId: selectedCompany.id } : {},
        }),
        axiosGetQuestions({
          token,
          params: selectedCompany ? { companyId: selectedCompany.id } : {},
        }),
      ]);
      console.log("Categories response:", categoriesRes.data);
      console.log("Skills response:", skillsRes.data);
      console.log("Questions response:", questionsRes.data);

      setAvailableCategories(categoriesRes.data || []);
      setAvailableSkills(skillsRes.data || []);
      setAvailableQuestions(questionsRes.data || []);
    } catch (error) {
      notify("Failed to load content", "error");
      console.error("Error loading content:", error);
    } finally {
      setIsBuilderLoading(false);
    }
  }, [token, notify, selectedCompany]);

  const openTemplates = async (companyId) => {
    setSelectedCompany(companies.find((c) => c.id === companyId));
    await loadTemplates(companyId);
    setShowTemplates(true);
  };

  const openTemplateBuilder = async (templateId) => {
    try {
      const response = await axiosGetCompanyTemplateDetails({
        token,
        url: `/${templateId}`,
      });
      setSelectedTemplate(response.data);

      // Set the selected company from the template data
      if (response.data.company) {
        setSelectedCompany(response.data.company);
      }

      // Convert template structure to builder format
      const builderStructure =
        response.data.category?.map((cat, index) => ({
          id: `category-${index}`,
          type: "category",
          data: cat.category,
          skills:
            cat.skills?.map((skill, skillIndex) => ({
              id: `skill-${index}-${skillIndex}`,
              type: "skill",
              data: skill.skill,
              questions:
                skill.questions?.map((question, qIndex) => ({
                  id: `question-${index}-${skillIndex}-${qIndex}`,
                  type: "question",
                  data: question,
                })) || [],
            })) || [],
          questions:
            cat.questions?.map((question, qIndex) => ({
              id: `question-${index}-${qIndex}`,
              type: "question",
              data: question,
            })) || [],
        })) || [];

      setTemplateStructure(builderStructure);
      setShowTemplateBuilder(true);

      // Load available content after setting the company
      await loadAvailableContent();
    } catch (error) {
      notify("Failed to load template for editing", "error");
      console.error("Error loading template for editing:", error);
    }
  };

  const saveTemplate = async () => {
    try {
      // Convert builder structure back to template format
      const updatedCategory = templateStructure.map((category) => ({
        category: category.data.id,
        skills:
          category.skills?.map((skill) => ({
            skill: skill.data.id,
            questions: skill.questions?.map((q) => q.data.id) || [],
          })) || [],
        questions: category.questions?.map((q) => q.data.id) || [],
      }));

      await axiosUpdateCompanyTemplate({
        token,
        url: `/${selectedTemplate.id}`,
        data: {
          category: updatedCategory,
        },
      });

      notify("Template updated successfully", "success");
      setShowTemplateBuilder(false);
      await loadTemplates(selectedCompany.id); // Reload templates
    } catch (error) {
      console.error("Error saving template:", error);
      notify(
        "Failed to save template: " +
          (error.response?.data?.message || error.message),
        "error"
      );
    }
  };

  const deleteTemplate = async (templateId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this template? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await axiosDeleteCompanyTemplate({ token, url: `/${templateId}` });
      notify("Template deleted successfully", "success");
      await loadTemplates(selectedCompany.id); // Reload templates
    } catch (error) {
      notify("Failed to delete template", "error");
      console.error("Error deleting template:", error);
    }
  };

  const createNewTemplate = async () => {
    if (!newTemplateTitle.trim()) {
      notify("Please enter a template title", "error");
      return;
    }

    try {
      const response = await axiosCreateCompanyTemplate({
        token,
        data: {
          title: newTemplateTitle.trim(),
          company: selectedCompany.id,
          category: [], // Start with empty template
        },
      });

      notify("Template created successfully", "success");
      setShowAddTemplate(false);
      setNewTemplateTitle("");

      // Reload templates to show the new one
      await loadTemplates(selectedCompany.id, false);

      // Open the new template for editing
      if (response.data && response.data.id) {
        await openTemplateBuilder(response.data.id);
      }
    } catch (error) {
      console.error("Error creating template:", error);
      notify(
        "Failed to create template: " +
          (error.response?.data?.message || error.message),
        "error"
      );
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, type) => {
    setDraggedItem({ ...item, type });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetCategoryId, targetSkillId = null) => {
    e.preventDefault();
    if (!draggedItem) return;

    const newStructure = [...templateStructure];

    if (draggedItem.type === "category") {
      // Add category to template
      const categoryToAdd = availableCategories.find(
        (cat) => cat.id === draggedItem.id
      );
      if (categoryToAdd) {
        newStructure.push({
          id: `category-${Date.now()}`,
          type: "category",
          data: categoryToAdd,
          skills: [],
          questions: [],
        });
      }
    } else if (
      draggedItem.type === "skill" &&
      targetCategoryId &&
      !targetSkillId
    ) {
      // Add skill to category (only in skills area, not direct questions area)
      const categoryIndex = newStructure.findIndex(
        (cat) => cat.id === targetCategoryId
      );
      if (categoryIndex !== -1) {
        const skillToAdd = availableSkills.find(
          (skill) => skill.id === draggedItem.id
        );
        if (skillToAdd) {
          newStructure[categoryIndex].skills.push({
            id: `skill-${Date.now()}`,
            type: "skill",
            data: skillToAdd,
            questions: [],
          });
        }
      }
    } else if (draggedItem.type === "question" && targetCategoryId) {
      const categoryIndex = newStructure.findIndex(
        (cat) => cat.id === targetCategoryId
      );
      if (categoryIndex !== -1) {
        const questionToAdd = availableQuestions.find(
          (q) => q.id === draggedItem.id
        );

        if (!questionToAdd) return;

        // Check if this is an input question (type: "Input")
        const isInputQuestion = questionToAdd.type === "Input";
        const category = newStructure[categoryIndex];

        if (isInputQuestion) {
          // Input questions can ONLY go in input type categories
          const isInputCategory = category.data.type === "input";

          if (!isInputCategory) {
            notify(
              "Input questions can only be placed in input type categories.",
              "error"
            );
            return;
          }

          if (targetSkillId) {
            // Input questions CANNOT go in skills
            notify(
              "Input questions cannot be placed in skills. They must go directly on input categories.",
              "error"
            );
            return;
          } else {
            // Add input question directly to input category
            newStructure[categoryIndex].questions.push({
              id: `question-${Date.now()}`,
              type: "question",
              data: questionToAdd,
            });
          }
        } else {
          // Non-input questions (Scale questions) can go in skills or directly on categories
          if (targetSkillId) {
            // Add question to skill
            const skillIndex = newStructure[categoryIndex].skills.findIndex(
              (skill) => skill.id === targetSkillId
            );
            if (skillIndex !== -1) {
              newStructure[categoryIndex].skills[skillIndex].questions.push({
                id: `question-${Date.now()}`,
                type: "question",
                data: questionToAdd,
              });
            }
          } else {
            // Add question directly to category
            newStructure[categoryIndex].questions.push({
              id: `question-${Date.now()}`,
              type: "question",
              data: questionToAdd,
            });
          }
        }
      }
    } else if (draggedItem.type === "skill" && targetSkillId) {
      // Skills cannot be dropped in the direct questions area
      notify(
        "Skills cannot be placed in the direct questions area. Use the skills area above.",
        "error"
      );
      return;
    }

    setTemplateStructure(newStructure);
    setDraggedItem(null);
  };

  const removeItem = (categoryId, skillId = null, questionId = null) => {
    const newStructure = [...templateStructure];

    if (questionId && skillId) {
      // Remove question from skill
      const categoryIndex = newStructure.findIndex(
        (cat) => cat.id === categoryId
      );
      if (categoryIndex !== -1) {
        const skillIndex = newStructure[categoryIndex].skills.findIndex(
          (skill) => skill.id === skillId
        );
        if (skillIndex !== -1) {
          newStructure[categoryIndex].skills[skillIndex].questions =
            newStructure[categoryIndex].skills[skillIndex].questions.filter(
              (q) => q.id !== questionId
            );
        }
      }
    } else if (questionId) {
      // Remove question from category
      const categoryIndex = newStructure.findIndex(
        (cat) => cat.id === categoryId
      );
      if (categoryIndex !== -1) {
        newStructure[categoryIndex].questions = newStructure[
          categoryIndex
        ].questions.filter((q) => q.id !== questionId);
      }
    } else if (skillId) {
      // Remove skill from category
      const categoryIndex = newStructure.findIndex(
        (cat) => cat.id === categoryId
      );
      if (categoryIndex !== -1) {
        newStructure[categoryIndex].skills = newStructure[
          categoryIndex
        ].skills.filter((skill) => skill.id !== skillId);
      }
    } else {
      // Remove category
      setTemplateStructure(newStructure.filter((cat) => cat.id !== categoryId));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Company Management</h1>
        <p className="mt-2 text-gray-600">
          Manage all companies and their configurations
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Companies List */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                All Companies ({companies.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-200">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className="px-6 py-4 hover:bg-gray-50 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="text-sm font-medium text-gray-900">
                        {company.name}
                      </h3>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          company.setup_complete
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {company.setup_complete ? "Active" : "Setup Required"}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      <p>
                        {company.country} • Created{" "}
                        {formatDate(company.created_at)}
                      </p>
                      <p>
                        {company.categories_count} categories •{" "}
                        {company.skills_count} skills
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openTemplates(company.id)}
                      className="text-green-600 hover:text-green-900 text-sm font-medium"
                    >
                      Templates
                    </button>
                    <button
                      onClick={() => handleEditCompany(company)}
                      className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDeleteCompany(company.id, company.name)
                      }
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Company Details / Edit Form */}
        <div className="lg:col-span-1">
          {editingCompany ? (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Company
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={editingCompany.name}
                    onChange={(e) =>
                      setEditingCompany({
                        ...editingCompany,
                        name: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <input
                    type="url"
                    value={editingCompany.website || ""}
                    onChange={(e) =>
                      setEditingCompany({
                        ...editingCompany,
                        website: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <input
                    type="text"
                    value={editingCompany.country || ""}
                    onChange={(e) =>
                      setEditingCompany({
                        ...editingCompany,
                        country: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={editingCompany.phone_number || ""}
                    onChange={(e) =>
                      setEditingCompany({
                        ...editingCompany,
                        phone_number: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingCompany(null)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : selectedCompany && showDetails ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Company Details
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">
                    Basic Information
                  </h4>
                  <div className="mt-2 text-sm text-gray-600">
                    <p>
                      <strong>Name:</strong> {selectedCompany.name}
                    </p>
                    <p>
                      <strong>Website:</strong>{" "}
                      {selectedCompany.website || "Not provided"}
                    </p>
                    <p>
                      <strong>Country:</strong>{" "}
                      {selectedCompany.country || "Not provided"}
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      {selectedCompany.phone_number || "Not provided"}
                    </p>
                    <p>
                      <strong>Created:</strong>{" "}
                      {formatDate(selectedCompany.createdAt)}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      {selectedCompany.setup_complete
                        ? "Active"
                        : "Setup Required"}
                    </p>
                  </div>
                </div>

                {selectedCompany.admin_users &&
                  selectedCompany.admin_users.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900">Admin Users</h4>
                      <div className="mt-2 space-y-2">
                        {selectedCompany.admin_users.map((user) => (
                          <div key={user.id} className="text-sm text-gray-600">
                            <p>
                              <strong>
                                {user.first_name} {user.last_name}
                              </strong>
                            </p>
                            <p>{user.email}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {selectedCompany.company_templates &&
                  selectedCompany.company_templates.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900">Templates</h4>
                      <div className="mt-2 space-y-2">
                        {selectedCompany.company_templates.map((template) => (
                          <div
                            key={template.id}
                            className="text-sm text-gray-600"
                          >
                            <p>
                              <strong>{template.title}</strong>
                            </p>
                            <p>Created {formatDate(template.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No company selected
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Select a company to view details or edit information
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Templates Modal */}
      {showTemplates && selectedCompany && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Templates for {selectedCompany.name}
                </h3>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {templates.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      No templates found for this company
                    </p>
                    <button
                      onClick={() => setShowAddTemplate(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
                    >
                      Add Template
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {template.title}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {template.categories_count} categories • Created{" "}
                            {formatDate(template.created_at)}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openTemplateBuilder(template.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteTemplate(template.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add Template Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setShowAddTemplate(true)}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        + Add New Template
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowTemplates(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Builder Modal */}
      {showTemplateBuilder && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-lg rounded-md bg-white max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Template: {selectedTemplate.title}
              </h3>
              <button
                onClick={() => setShowTemplateBuilder(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex">
              {/* Available Content Sidebar */}
              <div className="w-1/3 border-r pr-4 overflow-y-auto">
                <h4 className="font-medium text-gray-900 mb-3">
                  Available Content
                </h4>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-4">
                  <button
                    onClick={() => setActiveTab("categories")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "categories"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Categories
                  </button>
                  <button
                    onClick={() => setActiveTab("skills")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "skills"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Skills
                  </button>
                  <button
                    onClick={() => setActiveTab("questions")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "questions"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Questions
                  </button>
                </div>

                {/* Tab Content */}
                <div className="max-h-96 overflow-y-auto">
                  {/* Categories Tab */}
                  {activeTab === "categories" && (
                    <div className="space-y-2">
                      {isBuilderLoading ? (
                        <div className="text-sm text-gray-500">Loading...</div>
                      ) : (
                        availableCategories.map((category) => (
                          <div
                            key={category.id}
                            draggable
                            onDragStart={(e) =>
                              handleDragStart(e, category, "category")
                            }
                            className="p-2 bg-blue-50 border border-blue-200 rounded cursor-move hover:bg-blue-100"
                          >
                            <div className="flex items-center space-x-2">
                              {category.icon && (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${category.icon.url}`}
                                  alt={category.title}
                                  className="w-4 h-4 object-contain"
                                />
                              )}
                              <div className="text-sm font-medium text-blue-900">
                                {category.title || "Unknown Category"}
                              </div>
                            </div>
                            <div className="text-xs text-blue-600">
                              {category.text || "No description available"}
                            </div>
                            {category.palette && (
                              <div className="text-xs text-blue-500 mt-1">
                                Palette: {category.palette}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Skills Tab */}
                  {activeTab === "skills" && (
                    <div className="space-y-2">
                      {availableSkills.map((skill) => (
                        <div
                          key={skill.id}
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, skill, "skill")
                          }
                          className="p-2 bg-green-50 border border-green-200 rounded cursor-move hover:bg-green-100"
                        >
                          <div className="flex items-center space-x-2">
                            {skill.icon && (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${skill.icon.url}`}
                                alt={skill.title}
                                className="w-4 h-4 object-contain"
                              />
                            )}
                            <div className="text-sm font-medium text-green-900">
                              {skill.title || "Unknown Skill"}
                            </div>
                          </div>
                          <div className="text-xs text-green-600">
                            {skill.text || "No description available"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Questions Tab */}
                  {activeTab === "questions" && (
                    <div className="space-y-2">
                      {availableQuestions
                        .sort((a, b) => {
                          // Put input questions last
                          if (a.type === "Input" && b.type !== "Input")
                            return 1;
                          if (a.type !== "Input" && b.type === "Input")
                            return -1;
                          return 0;
                        })
                        .map((question) => {
                          const isInputQuestion = question.type === "Input";
                          return (
                            <div
                              key={question.id}
                              draggable
                              onDragStart={(e) =>
                                handleDragStart(e, question, "question")
                              }
                              className={`p-2 border rounded cursor-move hover:opacity-80 ${
                                isInputQuestion
                                  ? "bg-red-50 border-red-200"
                                  : "bg-yellow-50 border-yellow-200"
                              }`}
                            >
                              <div
                                className={`text-sm font-medium ${
                                  isInputQuestion
                                    ? "text-red-900"
                                    : "text-yellow-900"
                                }`}
                              >
                                {question.title ||
                                  question.text ||
                                  "Unknown Question"}
                                {isInputQuestion && (
                                  <span className="ml-2 text-xs bg-red-200 text-red-800 px-1 rounded">
                                    INPUT
                                  </span>
                                )}
                              </div>
                              {question.title &&
                                question.text &&
                                question.title !== question.text && (
                                  <div
                                    className={`text-xs ${
                                      isInputQuestion
                                        ? "text-red-600"
                                        : "text-yellow-600"
                                    }`}
                                  >
                                    {question.text}
                                  </div>
                                )}
                              {isInputQuestion && (
                                <div className="text-xs text-red-500 mt-1">
                                  ⚠️ Only for input type categories
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Structure */}
              <div className="flex-1 pl-4 overflow-y-auto">
                <h4 className="font-medium text-gray-900 mb-3">
                  Template Structure
                </h4>

                <div
                  className="min-h-96 border-2 border-dashed border-gray-300 rounded-lg p-4"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e)}
                >
                  {templateStructure.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      Drag categories here to build your template
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {templateStructure.map((category) => (
                        <div
                          key={category.id}
                          className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center space-x-2">
                              {category.data?.icon && (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${category.data.icon.url}`}
                                  alt={category.data.title}
                                  className="w-5 h-5 object-contain"
                                />
                              )}
                              <h6 className="font-medium text-gray-900">
                                {category.data?.title || "Unknown Category"}
                              </h6>
                              {category.data?.palette && (
                                <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                                  {category.data.palette}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => removeItem(category.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {category.data?.text || "No description available"}
                          </p>

                          {/* Skills in this category - Only for non-input categories */}
                          {category.data.type !== "input" && (
                            <div
                              className="min-h-20 border-2 border-dashed border-gray-300 rounded p-2 mb-2"
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, category.id)}
                            >
                              <div className="text-xs text-gray-500 mb-2">
                                Skills
                              </div>
                              {category.skills.length === 0 ? (
                                <div className="text-xs text-gray-400">
                                  No skills added
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {category.skills.map((skill) => (
                                    <div
                                      key={skill.id}
                                      className="bg-green-100 border border-green-200 rounded p-2"
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex items-center space-x-2">
                                          {skill.data?.icon && (
                                            <img
                                              src={`${process.env.REACT_APP_BACKEND_URL}${skill.data.icon.url}`}
                                              alt={skill.data.title}
                                              className="w-4 h-4 object-contain"
                                            />
                                          )}
                                          <div>
                                            <div className="text-sm font-medium text-green-900">
                                              {skill.data?.title ||
                                                "Unknown Skill"}
                                            </div>
                                            <div className="text-xs text-green-600">
                                              {skill.data?.text ||
                                                "No description available"}
                                            </div>
                                          </div>
                                        </div>
                                        <button
                                          onClick={() =>
                                            removeItem(category.id, skill.id)
                                          }
                                          className="text-red-600 hover:text-red-800 text-xs"
                                        >
                                          Remove
                                        </button>
                                      </div>

                                      {/* Questions in this skill */}
                                      <div
                                        className="mt-2 min-h-16 border-2 border-dashed border-gray-300 rounded p-2"
                                        onDragOver={handleDragOver}
                                        onDrop={(e) =>
                                          handleDrop(e, category.id, skill.id)
                                        }
                                      >
                                        <div className="text-xs text-gray-500 mb-1">
                                          Questions
                                        </div>
                                        {skill.questions.length === 0 ? (
                                          <div className="text-xs text-gray-400">
                                            No questions added
                                          </div>
                                        ) : (
                                          <div className="space-y-1">
                                            {skill.questions.map((question) => (
                                              <div
                                                key={question.id}
                                                className="bg-yellow-100 border border-yellow-200 rounded p-1 flex justify-between items-center"
                                              >
                                                <div className="text-xs text-yellow-900">
                                                  {question.data?.title ||
                                                    question.data?.text ||
                                                    "Unknown Question"}
                                                </div>
                                                <button
                                                  onClick={() =>
                                                    removeItem(
                                                      category.id,
                                                      skill.id,
                                                      question.id
                                                    )
                                                  }
                                                  className="text-red-600 hover:text-red-800 text-xs"
                                                >
                                                  ×
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Direct questions on category - Only for input categories */}
                          {category.data.type === "input" && (
                            <div
                              className="min-h-16 border-2 border-dashed border-green-300 bg-green-50 rounded p-2"
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, category.id)}
                            >
                              <div className="text-xs text-green-600 mb-1">
                                Input Questions
                              </div>
                              {category.questions.length === 0 ? (
                                <div className="text-xs text-gray-400">
                                  No questions added
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {category.questions.map((question) => (
                                    <div
                                      key={question.id}
                                      className="bg-yellow-100 border border-yellow-200 rounded p-1 flex justify-between items-center"
                                    >
                                      <div className="text-xs text-yellow-900">
                                        {question.data?.title ||
                                          question.data?.text ||
                                          "Unknown Question"}
                                      </div>
                                      <button
                                        onClick={() =>
                                          removeItem(
                                            category.id,
                                            null,
                                            question.id
                                          )
                                        }
                                        className="text-red-600 hover:text-red-800 text-xs"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex justify-end space-x-3 border-t pt-4">
              <button
                onClick={() => setShowTemplateBuilder(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Create New Template
                </h3>
                <button
                  onClick={() => {
                    setShowAddTemplate(false);
                    setNewTemplateTitle("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Title
                  </label>
                  <input
                    type="text"
                    value={newTemplateTitle}
                    onChange={(e) => setNewTemplateTitle(e.target.value)}
                    placeholder="Enter template title..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddTemplate(false);
                    setNewTemplateTitle("");
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={createNewTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
